import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import type { Expense } from '#/graphql/types';
import useExpenseQuery from '../hooks/useExpenseQuery';
import ExpenseForm from '../components/ExpenseForm';
import useExpenseUpdate from '../hooks/useExpenseUpdate';
import useExpenseDelete from '../hooks/useExpenseDelete';
import fields from '../components/ExpenseForm/fields';
import getFormTitle from '#/common/utils/getFormTitle';
import { SubmitExpense } from '../utils/types';

export interface ExpenseParams {
  readonly id?: string;
}

const ExpensePage: React.FC = () => {
  const { id } = useParams() as ExpenseParams;
  const expenseQuery = useExpenseQuery(id);
  const expense = expenseQuery.data?.getExpense?.data;

  const expenseNumber = expense?.displayNumber;
  const title = getFormTitle(id, expenseNumber, 'Expense');

  // Update Operation
  const { updateExpense } = useExpenseUpdate();
  const submit = useCallback<SubmitExpense>(
    async (expense: Expense) => {
      if (!id) {
        return undefined;
      }

      const { data, errors } = (await updateExpense(id, expense)) as {
        data?: Expense;
        errors?: string[];
      };

      return {
        data: data ?? undefined,
        errors: errors ?? undefined,
      };
    },
    [id, updateExpense],
  );

  // Delete Operation
  const { deleteExpense: delExpense } = useExpenseDelete();
  const deleteExpense = useCallback(() => {
    if (!id) {
      return undefined;
    }

    return delExpense(id);
  }, [delExpense, id]);

  return (
    <SubPage
      title={title || 'Expense'}
      isLoading={expenseQuery.loading}
      showGoBackButton
      type="form">
      {expense && (
        <ExpenseForm
          title={title}
          expense={expense}
          fields={fields}
          submit={submit}
          deleteExpense={deleteExpense}
        />
      )}
    </SubPage>
  );
};

export default React.memo(ExpensePage);
